import {set, get, del, createStore} from 'idb-keyval';
import {expose, wrap, Remote, windowEndpoint} from 'comlink';

// any object which has postMessage with targetOrigin parameters (windows/iframes)
interface PostMessageWithOrigin {
	postMessage(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- message can have any type
		message: any,
		targetOrigin: string,
		transfer?: Transferable[],
	): void;
}
// Any EventTarget like interface
export interface EventSource {
	addEventListener(
		type: string,
		listener: EventListenerOrEventListenerObject,
		// eslint-disable-next-line @typescript-eslint/ban-types
		options?: {},
	): void;

	removeEventListener(
		type: string,
		listener: EventListenerOrEventListenerObject,
		// eslint-disable-next-line @typescript-eslint/ban-types
		options?: {},
	): void;
}

/**
 * Types that can be used as storage keys
 */
type KeyTypes = string | number | Date;
/**
 * Types which can be stored in indexeddb and transferred across the postMessage boundary
 */
type ValueTypes = string | number | boolean | Date | Blob | ArrayBuffer;

/**
 * A proxy which can store data in a common origin.
 */
export interface ProxyStorage {
	/**
	 * Store a value in the proxy iframe origin.
	 */
	set: (filename: KeyTypes, value: ValueTypes) => Promise<void>;
	/**
	 * Retrieve a value from the proxy iframe origin.
	 */
	get: (filename: KeyTypes, deleteImmediately?: boolean) => Promise<ValueTypes | undefined>;
	/**
	 * Delete a value in the proxy iframe origin.
	 */
	del: (filename: KeyTypes) => Promise<void>;
}

/**
 * Creates the proxy on the specified window endpoint.
 *
 * @param id The ID to use for this proxy
 * @param w A window/iframe with postMessage capability
 * @param context A context on which to listen for events, often self / this
 * @param targetOrigin The origin to send messages to, often '*'
 */
export function createProxy(
	id: string,
	w: PostMessageWithOrigin,
	context: EventSource,
	...targetOrigins: string[]
): void {
	const customStore = createStore(`${id}-proxy`, 'keyval');
	const proxy: ProxyStorage = {
		set: async(filename: KeyTypes, value: ValueTypes): Promise<void> => {
			await set(filename, value, customStore);
		},
		get: async(filename: KeyTypes, deleteImmediately?: boolean): Promise<ValueTypes | undefined> => {
			const retrievalPromise = get(filename, customStore);
			if (deleteImmediately) {
				retrievalPromise.then(() => {
					del(filename, customStore);
				});
			}
			return retrievalPromise;
		},
		del: async(filename: KeyTypes): Promise<void> => del(filename),
	};

	targetOrigins.forEach((origin) => {
		expose(proxy, windowEndpoint(w, context, origin));
	});
}

/**
 * Get the ProxyStorage attached to a specific endpoint
 *
 * @param w A window/iframe with postMessage capability
 * @param context A context on which to listen for events, often self / this
 * @param targetOrigin The origin to send messages to, should be the origin of the iframe
 * @returns A ProxyStorage object
 */
export function getProxy(w: PostMessageWithOrigin, context: EventSource, targetOrigin: string): Remote<ProxyStorage> {
	return wrap<ProxyStorage>(windowEndpoint(w, context, targetOrigin));
}
